import { z } from 'zod'
import { AgentProvenanceEntrySchema } from './agent_provenance_entry.ts'
import { CompanyTeamEntrySchema } from './company_team_entry.ts'
import { TaskRecordSchema } from './task_record.ts'
import { UserEntrySchema } from './user_entry.ts'
import { RoundEntrySchema } from './round_entry.ts'
import { AccountRoleEntrySchema } from './account_role_entry.ts'
import { InvestmentStatsEntrySchema } from './investment_stats_entry.ts'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'
import { AccountsModelFields } from './accounts_model.ts'
import {
  TABLE_ACCOUNTS,
  TABLE_CONTACT_ACCOUNTS,
  TABLE_ROUNDS,
  FIELD_KEY_SEPARATOR,
  FIELD_KEY_PROG,
  FIELD_KEY_LABEL
} from '../globals.ts'

const ALIAS_ACCOUNTS = `${TABLE_ACCOUNTS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_CONTACT_ACCOUNTS = `${TABLE_CONTACT_ACCOUNTS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_ROUNDS = `${TABLE_ROUNDS}${FIELD_KEY_SEPARATOR}` as const

const PROG_COMPANY_TEAM = `${FIELD_KEY_PROG}company_team` as const
const PROG_EX_COMPANY_TEAM = `${FIELD_KEY_PROG}ex_company_team` as const
const PROG_OTHER_TEAM = `${FIELD_KEY_PROG}other_team` as const
const PROG_TASKS = `${FIELD_KEY_PROG}tasks` as const
const PROG_ROUNDS = `${FIELD_KEY_PROG}rounds` as const
const PROG_RECENT_ROUND = `${FIELD_KEY_PROG}recent_round` as const
const PROG_LATEST_ROUND = `${FIELD_KEY_PROG}latest_round` as const
const PROG_ACCOUNT_ROLES = `${FIELD_KEY_PROG}account_roles` as const
const PROG_INVESTMENT_STATS = `${FIELD_KEY_PROG}investment_stats` as const
const PROG_COMMITMENTS_COUNT = `${FIELD_KEY_PROG}commitments_count` as const
// Derived LP status (LP agent programme Phase 7): get_account_data_v3 resolves
// it through util__get_lp_status in single-account DETAIL mode only, so these
// are null on any grid read. Surfaced under the source cfg table's name — it
// is a rule-driven derivation, not a column on accounts. tag_color is cfg, so
// the LP states strip needs no colour map; null means render no tag.
const ALIAS_COMMS_LP_STATUSES = `comms_lp_statuses${FIELD_KEY_SEPARATOR}` as const
const FIELD_LP_STATUS_ID = `${ALIAS_COMMS_LP_STATUSES}id` as const
const FIELD_LP_STATUS_LABEL = `${ALIAS_COMMS_LP_STATUSES}label` as const
const FIELD_LP_STATUS_TAG_COLOR = `${ALIAS_COMMS_LP_STATUSES}tag_color` as const
const PROG_INVESTMENT_STATS_INVESTED = `${PROG_INVESTMENT_STATS}.invested` as const
const PROG_INVESTMENT_STATS_COST_BASIS = `${PROG_INVESTMENT_STATS}.cost_basis` as const
const PROG_INVESTMENT_STATS_REALIZED_VALUE = `${PROG_INVESTMENT_STATS}.realized_value` as const
const PROG_INVESTMENT_STATS_UNREALIZED_VALUE = `${PROG_INVESTMENT_STATS}.unrealized_value` as const
const PROG_INVESTMENT_STATS_MOIC = `${PROG_INVESTMENT_STATS}.moic` as const
const PROG_INVESTMENT_STATS_FIRST_INVESTED = `${PROG_INVESTMENT_STATS}.first_invested` as const
const FIELD_CONTACT_ACCOUNTS_INTRODUCED_BY = `${ALIAS_CONTACT_ACCOUNTS}introduced_by` as const
const FIELD_CONTACT_ACCOUNTS_SOURCED_BY = `${ALIAS_CONTACT_ACCOUNTS}sourced_by` as const

const FIELD_ACC_ID = `${ALIAS_ACCOUNTS}${AccountsModelFields.id}` as const
const FIELD_ACC_NAME = `${ALIAS_ACCOUNTS}${AccountsModelFields.name}` as const
const FIELD_ACC_LEGAL_NAME = `${ALIAS_ACCOUNTS}${AccountsModelFields.legal_name}` as const
const FIELD_ACC_ALIASES = `${ALIAS_ACCOUNTS}${AccountsModelFields.aliases}` as const
const FIELD_ACC_ACCOUNT_NUMBER = `${ALIAS_ACCOUNTS}${AccountsModelFields.account_number}` as const
const FIELD_ACC_PRIORITY = `${ALIAS_ACCOUNTS}${AccountsModelFields.priority}` as const
const FIELD_ACC_TRACK = `${ALIAS_ACCOUNTS}${AccountsModelFields.track}` as const
const FIELD_ACC_DESCRIPTION = `${ALIAS_ACCOUNTS}${AccountsModelFields.description}` as const
const FIELD_ACC_TEAM_NAME = `${ALIAS_ACCOUNTS}${AccountsModelFields.team_name}` as const
const FIELD_ACC_WEBSITE = `${ALIAS_ACCOUNTS}${AccountsModelFields.website}` as const
const FIELD_ACC_DOMAIN = `${ALIAS_ACCOUNTS}${AccountsModelFields.domain}` as const
const FIELD_ACC_INDUSTRY = `${ALIAS_ACCOUNTS}${AccountsModelFields.industry}` as const
const FIELD_ACC_CRITICAL_TECHNOLOGY_AREA = `${ALIAS_ACCOUNTS}${AccountsModelFields.critical_technology_area}` as const
const FIELD_ACC_INVESTOR_CLASSIFICATION_ID = `${ALIAS_ACCOUNTS}${AccountsModelFields.investor_classification_id}` as const
const FIELD_ACC_SHARE_REGISTER_ID = `${ALIAS_ACCOUNTS}${AccountsModelFields.share_register_id}` as const
const FIELD_ACC_INCORPORATION = `${ALIAS_ACCOUNTS}${AccountsModelFields.incorporation}` as const
const FIELD_ACC_FOUNDED_IN_COUNTRY_CODE = `${ALIAS_ACCOUNTS}${AccountsModelFields.founded_in_country_code}` as const
const FIELD_ACC_FOUNDED_IN_CITY_CODE = `${ALIAS_ACCOUNTS}${AccountsModelFields.founded_in_city_code}` as const
const FIELD_ACC_FOUNDED_IN_CITY_LABEL = `${ALIAS_ACCOUNTS}${AccountsModelFields.founded_in_city_label}` as const
const FIELD_ACC_FOUNDED_IN_STATEPROVINCE = `${ALIAS_ACCOUNTS}${AccountsModelFields.founded_in_stateprovince}` as const
const FIELD_ACC_BUSINESS_CITY_CODE = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_city_code}` as const
const FIELD_ACC_BUSINESS_CITY_LABEL = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_city_label}` as const
const FIELD_ACC_BUSINESS_COUNTRY_CODE = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_country_code}` as const
const FIELD_ACC_BUSINESS_COUNTRY_LABEL = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_country_label}` as const
const FIELD_ACC_BUSINESS_ADDRESS_1 = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_address_1}` as const
const FIELD_ACC_BUSINESS_ADDRESS_2 = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_address_2}` as const
const FIELD_ACC_BUSINESS_STATEPROVINCE = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_stateprovince}` as const
const FIELD_ACC_BUSINESS_POSTALCODE = `${ALIAS_ACCOUNTS}${AccountsModelFields.business_postalcode}` as const
const FIELD_ACC_OFFICE_CITY_CODE = `${ALIAS_ACCOUNTS}${AccountsModelFields.office_city_code}` as const
const FIELD_ACC_OFFICE_COUNTRY_CODE = `${ALIAS_ACCOUNTS}${AccountsModelFields.office_country_code}` as const
const FIELD_ACC_OFFICE_ADDRESS_1 = `${ALIAS_ACCOUNTS}${AccountsModelFields.office_address_1}` as const
const FIELD_ACC_OFFICE_ADDRESS_2 = `${ALIAS_ACCOUNTS}${AccountsModelFields.office_address_2}` as const
const FIELD_ACC_OFFICE_STATEPROVINCE = `${ALIAS_ACCOUNTS}${AccountsModelFields.office_stateprovince}` as const
const FIELD_ACC_OFFICE_POSTALCODE = `${ALIAS_ACCOUNTS}${AccountsModelFields.office_postalcode}` as const
const FIELD_ACC_SAME_AS_BUSINESS_ADDRESS = `${ALIAS_ACCOUNTS}${AccountsModelFields.same_as_business_address}` as const

// Agent Access 4.5: null unless an agent session created this record.
const PROG_CREATED_PROVENANCE = `${FIELD_KEY_PROG}created_provenance` as const

export const AccountRecordSchema = z
  .object({
    [FIELD_ACC_ID]: z.string().nullish(),
    [FIELD_ACC_NAME]: z.string().nullish(),
    [FIELD_ACC_LEGAL_NAME]: z.string().nullish(),
    [FIELD_ACC_ALIASES]: z.array(z.string()).nullish(),
    [FIELD_ACC_ACCOUNT_NUMBER]: z.string().nullish(),
    [FIELD_ACC_PRIORITY]: z.string().nullish(),
    [FIELD_ACC_TRACK]: z.boolean().nullish(),
    [FIELD_ACC_DESCRIPTION]: z.string().nullish(),
    [FIELD_ACC_TEAM_NAME]: z.string().nullish(),
    [FIELD_ACC_WEBSITE]: z.string().nullish(),
    [FIELD_ACC_DOMAIN]: z.string().nullish(),
    [FIELD_ACC_INDUSTRY]: z.string().nullish(),
    [FIELD_ACC_CRITICAL_TECHNOLOGY_AREA]: z.string().nullish(),
    [FIELD_ACC_INVESTOR_CLASSIFICATION_ID]: z.number().nullish(),
    [FIELD_ACC_SHARE_REGISTER_ID]: z.string().nullish(),
    [FIELD_ACC_INCORPORATION]: z.string().nullish(),
    [FIELD_ACC_FOUNDED_IN_COUNTRY_CODE]: z.string().nullish(),
    [FIELD_ACC_FOUNDED_IN_CITY_CODE]: z.number().nullish(),
    [FIELD_ACC_FOUNDED_IN_CITY_LABEL]: z.string().nullish(),
    [FIELD_ACC_FOUNDED_IN_STATEPROVINCE]: z.string().nullish(),
    [FIELD_ACC_BUSINESS_CITY_CODE]: z.number().nullish(),
    [FIELD_ACC_BUSINESS_CITY_LABEL]: z.string().nullish(),
    [FIELD_ACC_BUSINESS_COUNTRY_CODE]: z.string().nullish(),
    [FIELD_ACC_BUSINESS_COUNTRY_LABEL]: z.string().nullish(),
    [FIELD_ACC_BUSINESS_ADDRESS_1]: z.string().nullish(),
    [FIELD_ACC_BUSINESS_ADDRESS_2]: z.string().nullish(),
    [FIELD_ACC_BUSINESS_STATEPROVINCE]: z.string().nullish(),
    [FIELD_ACC_BUSINESS_POSTALCODE]: z.string().nullish(),
    [FIELD_ACC_OFFICE_CITY_CODE]: z.number().nullish(),
    [FIELD_ACC_OFFICE_COUNTRY_CODE]: z.string().nullish(),
    [FIELD_ACC_OFFICE_ADDRESS_1]: z.string().nullish(),
    [FIELD_ACC_OFFICE_ADDRESS_2]: z.string().nullish(),
    [FIELD_ACC_OFFICE_STATEPROVINCE]: z.string().nullish(),
    [FIELD_ACC_OFFICE_POSTALCODE]: z.string().nullish(),
    [FIELD_ACC_SAME_AS_BUSINESS_ADDRESS]: z.boolean().nullish(),

    [`${FIELD_ACC_INDUSTRY}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_ACC_CRITICAL_TECHNOLOGY_AREA}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    accounts__share_register_label: z.string().nullish(),
    [`${FIELD_ACC_OFFICE_CITY_CODE}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),
    [`${FIELD_ACC_OFFICE_COUNTRY_CODE}${FIELD_KEY_LABEL}` as const]: z.string().nullish(),

    [FIELD_CONTACT_ACCOUNTS_INTRODUCED_BY]: z.array(UserEntrySchema).nullish(), // TODO: needs to change to prog
    [FIELD_CONTACT_ACCOUNTS_SOURCED_BY]: z.array(UserEntrySchema).nullish(), // TODO: needs to change to prog
    [PROG_COMPANY_TEAM]: z.array(CompanyTeamEntrySchema).nullish(),
    [PROG_EX_COMPANY_TEAM]: z.array(CompanyTeamEntrySchema).nullish(),
    [PROG_OTHER_TEAM]: z.array(CompanyTeamEntrySchema).nullish(),
    [PROG_TASKS]: z.array(TaskRecordSchema).nullish(),
    [PROG_ROUNDS]: z.array(RoundEntrySchema).nullish(),
    [PROG_RECENT_ROUND]: RoundEntrySchema.nullish(),
    [PROG_LATEST_ROUND]: RoundEntrySchema.nullish(),
    [PROG_ACCOUNT_ROLES]: z.array(AccountRoleEntrySchema).nullish(),
    [PROG_INVESTMENT_STATS]: InvestmentStatsEntrySchema.nullish(),
    [PROG_COMMITMENTS_COUNT]: z.number().nullish(),
    [PROG_CREATED_PROVENANCE]: AgentProvenanceEntrySchema.nullish(),
    [FIELD_LP_STATUS_ID]: z.string().nullish(),
    [FIELD_LP_STATUS_LABEL]: z.string().nullish(),
    [FIELD_LP_STATUS_TAG_COLOR]: z.string().nullish(),
    // Per-LP agent drafts override (PR D) + what Default resolves to.
    accounts__agent_drafts_enabled: z.boolean().nullish(),
    prog__agent_drafts_default: z.string().nullish()
  })
  .passthrough()

export const AccountRecordFields = {
  ...makeFieldKeys(AccountRecordSchema),
  prog__investment_stats: PROG_INVESTMENT_STATS,
  prog__commitments_count: PROG_COMMITMENTS_COUNT,
  // Nested investment stats field paths for use in table columns
  prog__investment_stats_invested: PROG_INVESTMENT_STATS_INVESTED,
  prog__investment_stats_cost_basis: PROG_INVESTMENT_STATS_COST_BASIS,
  prog__investment_stats_realized_value: PROG_INVESTMENT_STATS_REALIZED_VALUE,
  prog__investment_stats_unrealized_value: PROG_INVESTMENT_STATS_UNREALIZED_VALUE,
  prog__investment_stats_moic: PROG_INVESTMENT_STATS_MOIC,
  prog__investment_stats_first_invested: PROG_INVESTMENT_STATS_FIRST_INVESTED
}

export type AccountRecord = z.infer<typeof AccountRecordSchema>

export function validateAccountRecord(data: unknown, options: { isArray: true }): AccountRecord[]
export function validateAccountRecord(data: unknown, options?: { isArray?: false }): AccountRecord
export function validateAccountRecord(data: unknown, options?: { isArray?: boolean }): AccountRecord | AccountRecord[]
export function validateAccountRecord(data: unknown, options?: { isArray?: boolean }): AccountRecord | AccountRecord[] {
  try {
    return validateWithZodSchema(AccountRecordSchema, data, 'account record', options)
  } catch (error) {
    throw new Error(`Account record validation failed: ${error}`)
  }
}
